import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { VotingPoll, VotingPollStatus, Vote, VotingResult } from '../../types/voting';
import { GraphQLVotingService } from '../../services/graphqlVoting';
import { useAuth } from '../../contexts/AuthContext';
import { canCreateVotingPoll } from '../../utils/nominationPermissions';
import { useNominationRole } from '../../hooks/useNominationRole';
import { loadAllUsers, SimpleUser } from '../../services/userLoader';
import { emailVoterAdded, emailVoterRemoved } from '../../services/emailNotification';
import PageHeader from '../common/PageHeader';
import './VotingPollDetail.css';

const VotingPollDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [poll, setPoll] = useState<VotingPoll | null>(null);
  const [votes, setVotes] = useState<Vote[]>([]);
  const [results, setResults] = useState<VotingResult[]>([]);
  const [selectedNominee, setSelectedNominee] = useState('');
  const [hasVoted, setHasVoted] = useState(false);
  const [myVote, setMyVote] = useState<Vote | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [loading, setLoading] = useState(true);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editVoters, setEditVoters] = useState<string[]>([]);
  const [editVoterNames, setEditVoterNames] = useState<string[]>([]);
  const [allUsers, setAllUsers] = useState<SimpleUser[]>([]);
  const [voterSearch, setVoterSearch] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const { nominationRole } = useNominationRole(user);
  const isCreatorOrAdmin = canCreateVotingPoll(nominationRole);
  const isOwner = poll?.createdBy === user?.email;
  const isVoter = poll?.voters.includes(user?.email || '') || false;
  const canSeeResults = isOwner || isCreatorOrAdmin;

  const loadPoll = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const pollData = await GraphQLVotingService.getVotingPoll(id);
    setPoll(pollData || null);

    if (pollData && (pollData.createdBy === user?.email || canCreateVotingPoll(nominationRole))) {
      const votesData = await GraphQLVotingService.getVotes(id);
      setVotes(votesData);
      setResults(GraphQLVotingService.computeResults(votesData, pollData.nomineeNames, pollData.nominees));
    }

    // Check if current user already voted
    if (pollData && user) {
      const allVotes = await GraphQLVotingService.getVotes(id);
      const existing = allVotes.find(v => v.voterId === user.email);
      if (existing) {
        setHasVoted(true);
        setMyVote(existing);
      }
      if (pollData.createdBy === user.email || canCreateVotingPoll(nominationRole)) {
        setVotes(allVotes);
        setResults(GraphQLVotingService.computeResults(allVotes, pollData.nomineeNames, pollData.nominees));
      }
    }
    setLoading(false);
  }, [id, user, nominationRole]);

  useEffect(() => { loadPoll(); }, [loadPoll]);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleVote = async () => {
    if (!poll || !user || !selectedNominee) return;
    setSubmitting(true);
    const nomineeName = poll.nomineeNames[poll.nominees.indexOf(selectedNominee)] || selectedNominee;
    const result = await GraphQLVotingService.castVote(poll.id, user.email, user.name, selectedNominee, nomineeName);
    setSubmitting(false);
    if (result.success) {
      showToast('Vote cast successfully', 'success');
      setHasVoted(true);
      setMyVote({ pollId: poll.id, voterId: user.email, voterName: user.name, nomineeId: selectedNominee, nomineeName, createdAt: new Date().toISOString() });
      loadPoll();
    } else {
      showToast(result.errors.join(', '), 'error');
    }
  };

  const handleClose = async () => {
    if (!poll || !window.confirm('Close this poll? No more votes will be accepted.')) return;
    const result = await GraphQLVotingService.closePoll(poll.id);
    if (result.success) { showToast('Poll closed', 'success'); loadPoll(); }
  };

  const openEditModal = async () => {
    if (!poll) return;
    console.log('[VotingPollDetail] Opening edit modal for poll:', poll.id);
    console.log('[VotingPollDetail] Current voters:', poll.voters);
    console.log('[VotingPollDetail] Current voterNames:', poll.voterNames);
    setEditVoters([...poll.voters]);
    setEditVoterNames([...poll.voterNames]);
    const users = await loadAllUsers();
    console.log('[VotingPollDetail] Loaded all users:', users.length);
    const filteredUsers = users.filter(u => u.email !== user?.email && !poll.nominees.includes(u.email));
    console.log('[VotingPollDetail] Filtered users (excluding current user and nominees):', filteredUsers.length);
    setAllUsers(filteredUsers);
    setShowEditModal(true);
  };

  const toggleEditVoter = (email: string, name: string, action?: 'add' | 'remove') => {
    console.log('[VotingPollDetail] toggleEditVoter called:', { email, name, action, currentVoters: editVoters.length });

    const index = editVoters.indexOf(email);

    // If action is explicitly specified, use it
    if (action === 'remove' && index !== -1) {
      // Remove voter
      const newVoters = [...editVoters];
      const newVoterNames = [...editVoterNames];
      newVoters.splice(index, 1);
      newVoterNames.splice(index, 1);
      setEditVoters(newVoters);
      setEditVoterNames(newVoterNames);
      console.log('[VotingPollDetail] Removed voter:', email, 'Remaining:', newVoters.length);
      showToast(`Removed ${name} from voters`, 'success');
      return;
    }

    if (action === 'add' && index === -1) {
      // Add voter
      const newVoters = [...editVoters, email];
      const newVoterNames = [...editVoterNames, name];
      setEditVoters(newVoters);
      setEditVoterNames(newVoterNames);
      console.log('[VotingPollDetail] Added voter:', email, name, 'Total:', newVoters.length);
      showToast(`Added ${name} to voters`, 'success');
      return;
    }

    // Fallback to toggle behavior if no action specified
    if (index !== -1) {
      // Remove voter
      const newVoters = [...editVoters];
      const newVoterNames = [...editVoterNames];
      newVoters.splice(index, 1);
      newVoterNames.splice(index, 1);
      setEditVoters(newVoters);
      setEditVoterNames(newVoterNames);
      console.log('[VotingPollDetail] Removed voter:', email, 'Remaining:', newVoters.length);
      showToast(`Removed ${name} from voters`, 'success');
    } else {
      // Add voter
      const newVoters = [...editVoters, email];
      const newVoterNames = [...editVoterNames, name];
      setEditVoters(newVoters);
      setEditVoterNames(newVoterNames);
      console.log('[VotingPollDetail] Added voter:', email, name, 'Total:', newVoters.length);
      showToast(`Added ${name} to voters`, 'success');
    }
  };

  const handleSaveEdit = async () => {
    if (!poll || !user) return;
    console.log('[VotingPollDetail] Saving poll edits...');
    console.log('[VotingPollDetail] New voters:', editVoters);
    console.log('[VotingPollDetail] New voterNames:', editVoterNames);
    setEditSaving(true);

    // Detect added and removed voters before saving
    const addedVoters = editVoters.filter(email => !poll.voters.includes(email));
    const removedVoters = poll.voters.filter(email => !editVoters.includes(email));

    const result = await GraphQLVotingService.updatePoll(poll.id, {
      voters: editVoters,
      voterNames: editVoterNames,
    }, user.name);
    setEditSaving(false);
    console.log('[VotingPollDetail] Update result:', result);
    if (result.success) {
      // Send email notifications to added/removed voters
      if (addedVoters.length > 0) {
        emailVoterAdded(addedVoters, poll.title, user.name, poll.id);
      }
      if (removedVoters.length > 0) {
        emailVoterRemoved(removedVoters, poll.title, user.name);
      }
      showToast('Poll updated successfully', 'success');
      setShowEditModal(false);
      loadPoll();
    } else {
      showToast(result.errors.join(', '), 'error');
    }
  };

  const filteredEditUsers = allUsers.filter(u => {
    // Exclude users who are already voters
    if (editVoters.includes(u.email)) {
      return false;
    }
    // Apply search filter
    if (voterSearch.trim() === '') {
      return true;
    }
    const searchLower = voterSearch.toLowerCase();
    return u.name.toLowerCase().includes(searchLower) ||
      u.email.toLowerCase().includes(searchLower) ||
      u.department?.toLowerCase().includes(searchLower);
  });

  console.log('[VotingPollDetail] Filtered edit users count:', filteredEditUsers.length, 'Search:', voterSearch);

  if (loading) return <div className="vpd-page"><div className="vpd-loading">Loading...</div></div>;
  if (!poll) return <div className="vpd-page"><div className="vpd-loading">Poll not found</div></div>;

  const totalVotes = votes.length;
  const totalVoters = poll.voters.length;
  const maxVotes = results.length > 0 ? results[0].voteCount : 0;

  return (
    <div className="vpd-page">
      <PageHeader
        icon={<span style={{ fontSize: '1.5rem' }}>🗳️</span>}
        title={poll.title}
        subtitle={poll.description || `Created by ${poll.createdByName}`}
        badge={poll.status}
        badgeColor={poll.status === VotingPollStatus.ACTIVE ? '#16a34a' : '#dc2626'}
        gradient="linear-gradient(135deg, #7c3aed, #6d28d9)"
        accentColor="#7c3aed"
        actions={
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="vpd-back-btn" onClick={() => navigate('/voting')}>← Back</button>
            {isOwner && poll.status === VotingPollStatus.ACTIVE && (
              <button className="vpd-edit-btn" onClick={openEditModal}>Edit Poll</button>
            )}
            {isOwner && poll.status === VotingPollStatus.ACTIVE && (
              <button className="vpd-close-btn" onClick={handleClose}>Close Poll</button>
            )}
          </div>
        }
      />

      {toast && <div className={`vpd-toast ${toast.type}`}>{toast.message}</div>}

      <div className="vpd-content">
        {/* Poll Info */}
        <div className="vpd-info-card">
          <div className="vpd-info-row">
            <span className="vpd-info-label">Status</span>
            <span className={`vp-status ${poll.status.toLowerCase()}`}>{poll.status}</span>
          </div>
          <div className="vpd-info-row">
            <span className="vpd-info-label">Created by</span>
            <span>{poll.createdByName}</span>
          </div>
          {poll.deadline && (
            <div className="vpd-info-row">
              <span className="vpd-info-label">Deadline</span>
              <span>{new Date(poll.deadline).toLocaleString()}</span>
            </div>
          )}
          <div className="vpd-info-row">
            <span className="vpd-info-label">Nominees</span>
            <div className="vpd-chips">{poll.nomineeNames.map((n, i) => <span key={i} className="vpd-chip nominee">{n}</span>)}</div>
          </div>
          <div className="vpd-info-row">
            <span className="vpd-info-label">Voters</span>
            <div className="vpd-chips">{poll.voterNames.map((n, i) => <span key={i} className="vpd-chip voter">{n}</span>)}</div>
          </div>
        </div>

        {/* Voting Section (for voters) */}
        {isVoter && poll.status === VotingPollStatus.ACTIVE && !hasVoted && (
          <div className="vpd-vote-card">
            <h3 className="vpd-section-title">Cast Your Vote</h3>
            <p className="vpd-vote-hint">Select one nominee to vote for:</p>
            <div className="vpd-vote-options">
              {poll.nominees.map((nomineeId, i) => (
                <label key={nomineeId} className={`vpd-vote-option ${selectedNominee === nomineeId ? 'selected' : ''}`}>
                  <input type="radio" name="vote" value={nomineeId} checked={selectedNominee === nomineeId} onChange={() => setSelectedNominee(nomineeId)} />
                  <div className="vpd-vote-avatar">{poll.nomineeNames[i]?.charAt(0)?.toUpperCase() || '?'}</div>
                  <span className="vpd-vote-name">{poll.nomineeNames[i]}</span>
                </label>
              ))}
            </div>
            <button className="vpd-vote-btn" onClick={handleVote} disabled={!selectedNominee || submitting}>
              {submitting ? 'Submitting...' : 'Submit Vote'}
            </button>
          </div>
        )}

        {/* Already voted */}
        {isVoter && hasVoted && (
          <div className="vpd-voted-card">
            <div className="vpd-voted-icon">✅</div>
            <h3>You've already voted</h3>
            <p>You voted for <strong>{myVote?.nomineeName}</strong></p>
          </div>
        )}

        {/* Results (only for creator / reviewer / admin) */}
        {canSeeResults && (
          <div className="vpd-results-card">
            <h3 className="vpd-section-title">Voting Results</h3>
            <p className="vpd-results-summary">{totalVotes} of {totalVoters} voters have voted</p>
            <div className="vpd-results-list">
              {results.map((r, i) => (
                <div key={r.nomineeId} className={`vpd-result-row ${i === 0 && r.voteCount > 0 ? 'leader' : ''}`}>
                  <div className="vpd-result-rank">{i + 1}</div>
                  <div className="vpd-result-info">
                    <span className="vpd-result-name">{r.nomineeName}</span>
                    <div className="vpd-result-bar-bg">
                      <div className="vpd-result-bar" style={{ width: maxVotes > 0 ? `${(r.voteCount / maxVotes) * 100}%` : '0%' }} />
                    </div>
                  </div>
                  <span className="vpd-result-count">{r.voteCount} vote{r.voteCount !== 1 ? 's' : ''}</span>
                </div>
              ))}
            </div>

            {/* Individual votes breakdown */}
            {votes.length > 0 && (
              <div className="vpd-votes-detail">
                <h4 className="vpd-detail-title">Individual Votes</h4>
                {votes.map((v, i) => (
                  <div key={i} className="vpd-vote-entry">
                    <span className="vpd-vote-voter">{v.voterName}</span>
                    <span className="vpd-vote-arrow">→</span>
                    <span className="vpd-vote-nominee">{v.nomineeName}</span>
                    <span className="vpd-vote-time">{new Date(v.createdAt).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="vpd-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="vpd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vpd-modal-header">
              <h3>Edit Voting Poll</h3>
              <button className="vpd-modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="vpd-modal-body">
              <div className="vpd-edit-section">
                <label className="vpd-edit-label">
                  Current Voters {editVoters.length > 0 && <span style={{ color: '#7c3aed', fontWeight: 700 }}>({editVoters.length})</span>}
                </label>
                <div className="vpd-edit-chips">
                  {editVoters.length === 0 ? (
                    <p style={{ color: '#94a3b8', fontSize: '0.88rem', fontStyle: 'italic' }}>No voters added yet</p>
                  ) : (
                    editVoters.map((email, i) => (
                      <span key={email} className="vpd-edit-chip">
                        {editVoterNames[i] || email}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('[VotingPollDetail] Remove button clicked for:', email);
                            toggleEditVoter(email, editVoterNames[i], 'remove');
                          }}
                          aria-label={`Remove ${editVoterNames[i] || email}`}
                        >
                          ×
                        </button>
                      </span>
                    ))
                  )}
                </div>
              </div>
              <div className="vpd-edit-section">
                <label className="vpd-edit-label">Add Voters</label>
                <input
                  type="text"
                  className="vpd-edit-search"
                  placeholder="Search users by name, email, or department..."
                  value={voterSearch}
                  onChange={(e) => setVoterSearch(e.target.value)}
                />
                <div className="vpd-edit-user-list">
                  {filteredEditUsers.length === 0 ? (
                    <div className="vpd-edit-no-users">
                      {voterSearch ? 'No users found matching your search' : 'All users have been added as voters'}
                    </div>
                  ) : (
                    filteredEditUsers.slice(0, 10).map(u => (
                      <div
                        key={u.email}
                        className="vpd-edit-user"
                      >
                        <div className="vpd-edit-user-info">
                          <span className="vpd-edit-user-name">{u.name}</span>
                          <span className="vpd-edit-user-email">{u.email}</span>
                          {u.department && <span className="vpd-edit-user-dept">{u.department}</span>}
                        </div>
                        <button
                          type="button"
                          className="vpd-edit-user-add"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('[VotingPollDetail] Add button clicked for:', u.email);
                            toggleEditVoter(u.email, u.name, 'add');
                          }}
                        >
                          + Add
                        </button>
                      </div>
                    ))
                  )}
                  {filteredEditUsers.length > 10 && (
                    <div className="vpd-edit-more">
                      {filteredEditUsers.length - 10} more users available. Use search to find them.
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="vpd-modal-footer">
              <button className="vpd-modal-cancel" onClick={() => setShowEditModal(false)}>Cancel</button>
              <button className="vpd-modal-save" onClick={handleSaveEdit} disabled={editSaving || editVoters.length === 0}>
                {editSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VotingPollDetail;
